import React from 'react'
import { Aircraft, Transaction } from '../../../types/intellijetnet'
import { AircraftOverride, AircraftTransaction, TransactionOverride, TakePriceModifier } from '../../../types/database'
import { ConsolidatedTransaction } from '../../../utils/transaction-consolidation'
import { formatPrice, formatAskingPrice, formatDate } from '../../../utils/formatters'
import { PriceWithModifier } from '../../shared/PriceWithModifier'
import InlinePriceEditor from '../../inline-editors/InlinePriceEditor'
import InlineAskPriceEditor from '../../inline-editors/InlineAskPriceEditor'
import InlineTakePriceEditor from '../../inline-editors/InlineTakePriceEditor'
import type { EditingCell, EditingCellFieldName } from '../types'
import type { EffectiveTransactionAircraft } from '../../../hooks/useMarketTableData'

interface SoldPricingCellsProps {
  aircraft: Aircraft
  effectiveAircraft: EffectiveTransactionAircraft
  isEditMode?: boolean
  cellBorderClass: string
  overrides: Map<number, AircraftOverride>
  transactionOverrides: Map<string, TransactionOverride>
  editingCell: EditingCell | null
  transaction?: Transaction | null
  aircraftTransaction?: AircraftTransaction | null
  hideAskingPrice: boolean
  hideExternalColumns: boolean
  getEffectiveTransactionData: (aircraftTransaction: AircraftTransaction | null | undefined, transaction: Transaction | null | undefined) => { asking_price: any; sale_price: any }
  handleStartTransactionEdit: (aircraftId: number, transactionId: string, fieldName: EditingCellFieldName) => void
  handleCancelEdit: () => void
  handleTransactionPriceSave: (transactionId: string, fieldName: EditingCellFieldName, newValue: number | null) => Promise<void>
  handleTransactionTakePriceSave: (transactionId: string, priceFieldName: 'take_price' | 'external_take_price', newValue: number | null, newModifier: TakePriceModifier | null) => Promise<void>
  handleTransactionAskPriceSave: (transactionId: string, priceFieldName: 'asking_price' | 'external_asking_price', newValue: number | null, newMakeOffer: boolean | null) => Promise<void>
}

export function SoldPricingCells(props: SoldPricingCellsProps) {
  const { aircraft, isEditMode, cellBorderClass, transactionOverrides, editingCell,
    transaction, aircraftTransaction, hideAskingPrice, hideExternalColumns,
    getEffectiveTransactionData,
    handleStartTransactionEdit, handleCancelEdit,
    handleTransactionPriceSave, handleTransactionTakePriceSave, handleTransactionAskPriceSave } = props

  const editClass = (hasOvr: boolean) => `${hasOvr ? 'text-red-800 font-medium' : ''} ${isEditMode && aircraftTransaction ? 'cursor-pointer hover:bg-green-50 rounded px-1 -mx-1' : ''}`
  const startTransEdit = (field: EditingCellFieldName) => isEditMode && aircraftTransaction
    ? (e: React.MouseEvent) => { e.stopPropagation(); handleStartTransactionEdit(aircraft.aircraft_id, aircraftTransaction.transaction_id, field); }
    : undefined

  return (
    <>
      {/* Date Listed / Date Sold */}
      <td className={`px-2 py-2 text-sm text-gray-900 text-center ${cellBorderClass}`}>
        <div>{aircraftTransaction ? formatDate(aircraftTransaction.date_listed || aircraft.datelisted) : ((transaction as Transaction)?.list_date ? formatDate((transaction as Transaction)?.list_date) : 'Off Mkt')}</div>
        <div>{formatDate(aircraftTransaction?.transaction_date || (transaction as Transaction)?.trans_date)}</div>
        {(() => {
          const listedDateStr = aircraftTransaction ? (aircraftTransaction.date_listed || aircraft.datelisted) : (transaction as Transaction)?.list_date
          const soldDateStr = aircraftTransaction?.transaction_date || (transaction as Transaction)?.trans_date
          if (listedDateStr && soldDateStr) {
            const daysDiff = Math.floor((new Date(soldDateStr).getTime() - new Date(listedDateStr).getTime()) / (1000 * 60 * 60 * 24))
            return <div className="text-gray-500 text-xs">{daysDiff}d</div>
          }
          return null
        })()}
        {(transaction as ConsolidatedTransaction)?.is_consolidated && (
          <div className="text-blue-600 text-xs font-medium mt-1" title={`Consolidated from ${(transaction as ConsolidatedTransaction).consolidation_count} transactions: ${(transaction as ConsolidatedTransaction).source_transaction_ids.join(', ')}`}>
            📊 ({(transaction as ConsolidatedTransaction).consolidation_count})
          </div>
        )}
      </td>

      {/* Ask / Sale Price */}
      {!hideAskingPrice && (
        <td className={`px-2 py-2 text-sm text-gray-900 text-center ${cellBorderClass}`}>
          {(() => {
            const effectiveData = getEffectiveTransactionData(aircraftTransaction, transaction)
            const transOverride = aircraftTransaction ? transactionOverrides.get(aircraftTransaction.transaction_id) : null
            const actualAskMakeOffer = transOverride?.actual_ask_make_offer ?? aircraftTransaction?.actual_ask_make_offer
            const hasAskOvr = transOverride?.asking_price != null || transOverride?.actual_ask_make_offer != null
            const hasSaleOvr = transOverride?.sale_price != null

            return (
              <>
                <div className={editClass(hasAskOvr)} onClick={startTransEdit('asking_price')}>
                  {editingCell?.transactionId === aircraftTransaction?.transaction_id && editingCell?.fieldName === 'asking_price' ? (
                    <InlineAskPriceEditor value={transOverride?.asking_price ?? aircraftTransaction?.asking_price ?? null} makeOffer={transOverride?.actual_ask_make_offer ?? aircraftTransaction?.actual_ask_make_offer ?? null} fieldName="asking_price"
                      onSave={(v, mo) => handleTransactionAskPriceSave(aircraftTransaction!.transaction_id, 'asking_price', v, mo)} onCancel={handleCancelEdit} />
                  ) : actualAskMakeOffer ? 'M.O.' : formatAskingPrice((transOverride?.asking_price ?? effectiveData.asking_price)?.toString())}
                </div>
                <div className={editClass(hasSaleOvr)} onClick={startTransEdit('sale_price')}>
                  {editingCell?.transactionId === aircraftTransaction?.transaction_id && editingCell?.fieldName === 'sale_price' ? (
                    <InlinePriceEditor value={transOverride?.sale_price ?? aircraftTransaction?.sale_price ?? null} fieldName="sale_price"
                      onSave={(v) => handleTransactionPriceSave(aircraftTransaction!.transaction_id, 'sale_price', v)} onCancel={handleCancelEdit} />
                  ) : formatPrice((transOverride?.sale_price ?? effectiveData.sale_price)?.toString())}
                </div>
              </>
            )
          })()}
        </td>
      )}

      {/* External Prices */}
      {!hideExternalColumns && (
        <td className={`px-2 py-2 text-sm text-gray-900 text-center ${cellBorderClass}`}>
          {(() => {
            if (aircraftTransaction) {
              const transOverride = transactionOverrides.get(aircraftTransaction.transaction_id)
              const adjAskMO = transOverride?.adj_ask_make_offer ?? aircraftTransaction.adj_ask_make_offer
              const hasExtAskOvr = (transOverride?.external_asking_price != null) || (transOverride?.adj_ask_make_offer != null)
              const hasExtTakeOvr = (transOverride?.external_take_price != null) || (transOverride?.external_take_price_modifier != null)
              const extAsk = transOverride?.external_asking_price ?? aircraftTransaction.external_asking_price ?? aircraftTransaction.asking_price
              const extTake = transOverride?.external_take_price ?? aircraftTransaction.external_take_price ?? aircraftTransaction.take_price
              const extTakeMod = transOverride?.external_take_price_modifier ?? aircraftTransaction.external_take_price_modifier ?? aircraftTransaction.take_price_modifier
              return (
                <>
                  <div className={editClass(hasExtAskOvr)} onClick={startTransEdit('external_asking_price')}>
                    {editingCell?.transactionId === aircraftTransaction.transaction_id && editingCell?.fieldName === 'external_asking_price' ? (
                      <InlineAskPriceEditor value={transOverride?.external_asking_price ?? aircraftTransaction.external_asking_price ?? null} makeOffer={transOverride?.adj_ask_make_offer ?? aircraftTransaction.adj_ask_make_offer ?? null} fieldName="external_asking_price"
                        onSave={(v, mo) => handleTransactionAskPriceSave(aircraftTransaction.transaction_id, 'external_asking_price', v, mo)} onCancel={handleCancelEdit} />
                    ) : adjAskMO ? 'M.O.' : formatAskingPrice(extAsk)}
                  </div>
                  <div className={editClass(hasExtTakeOvr)} onClick={startTransEdit('external_take_price')}>
                    {editingCell?.transactionId === aircraftTransaction.transaction_id && editingCell?.fieldName === 'external_take_price' ? (
                      <InlineTakePriceEditor value={transOverride?.external_take_price ?? aircraftTransaction.external_take_price ?? null} modifier={transOverride?.external_take_price_modifier ?? aircraftTransaction.external_take_price_modifier ?? null} fieldName="external_take_price"
                        onSave={(v, m) => handleTransactionTakePriceSave(aircraftTransaction.transaction_id, 'external_take_price', v, m)} onCancel={handleCancelEdit} />
                    ) : <PriceWithModifier value={extTake} modifier={extTakeMod} />}
                  </div>
                </>
              )
            } else {
              const override = props.overrides.get(aircraft.aircraft_id)
              return (
                <>
                  <div className={override?.external_asking_price != null || override?.adj_ask_make_offer != null ? 'text-red-800 font-medium' : ''}>
                    {override?.adj_ask_make_offer ? 'M.O.' : formatAskingPrice(override?.external_asking_price ?? props.effectiveAircraft.asking)}
                  </div>
                  <div className={override?.external_take_price != null || override?.external_take_price_modifier != null ? 'text-red-800 font-medium' : ''}>
                    <PriceWithModifier value={override?.external_take_price ?? props.effectiveAircraft.take_price} modifier={override?.external_take_price_modifier} />
                  </div>
                </>
              )
            }
          })()}
        </td>
      )}
    </>
  )
}

